import {SidebarDialog} from '@ui/SidebarDialog/SidebarDialog';
import {Dispatch, SetStateAction} from 'react';
import {Box, Stack, Typography} from '@mui/material';
import {DT_FORMATS, formatDate} from 'utils';
import {useEnumDictionary} from 'utils/hooks/useEnumDictionary';
import {priorityCellColor} from '../TechnicalSupportTable/TechnicalSupportTablePriorityCell';
import {ItemTitle} from './ItemTitle';
import styles from './TechnicalSupportIssueSidebar.module.scss';
import {TechnicalSupportIssueStatus} from './TechnicalSupportIssueStatus';
import {FileCard} from '@features/files/FileCard';
import {FileEntity} from '@pages/FilesPage';
import {useTranslation} from 'react-i18next';
import {useQuery} from '@tanstack/react-query';
import {TechnicalSupportService} from '@services/TechnicalSupport/TechnicalSupportService';
import {TechnicalSupportIssueStatusHistory} from './TechnicalSupportIssueStatusHistory';

type TechnicalSupportIssueSidebarProps = {
    selectedItemId: string;
    setSelectedItemId: (data: string | null) => void;
    setForceRefetch: Dispatch<SetStateAction<number>>;
};

export const TechnicalSupportIssueSidebar = ({
    setSelectedItemId,
    selectedItemId,
    setForceRefetch,
}: TechnicalSupportIssueSidebarProps) => {
    const {t} = useTranslation(['administration', 'common']);
    const onClose = () => {
        setSelectedItemId(null);
    };

    const afterSaveStatus = () => {
        setForceRefetch((prev) => prev + 1);
        onClose();
    };

    const {data: selectedItem} = useQuery({
        queryKey: ['getTechnicalIssue', selectedItemId],
        queryFn: () => TechnicalSupportService.getTechnicalIssue(selectedItemId),
        gcTime: 0,
    });

    const {getOptionNameByCode: getOptionNameByCodePriority} = useEnumDictionary({
        code: 'SupportTicketPriority',
    });
    const {getOptionNameByCode: getOptionNameByCodeType} = useEnumDictionary({
        code: 'SupportTicketType',
    });

    if (!selectedItem) return null;

    // Последний статус из истории
    const currentStatus = selectedItem.histories[selectedItem.histories.length - 1].status;

    return (
        <SidebarDialog
            open
            onClose={onClose}
            maxWidth='600px'
            customTitleBlock={
                <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    gap='16px'
                >
                    <Typography variant='subtitle1'>
                        {t('technicalSupport.issueFrom')}{' '}
                        {formatDate(selectedItem.createdDate, DT_FORMATS.default)}
                    </Typography>
                    <Box
                        component='div'
                        sx={{
                            color: priorityCellColor[selectedItem.priority],
                            border: `1px solid ${priorityCellColor[selectedItem.priority]}`,
                            borderRadius: '16px',
                            fontSize: '13px',
                            padding: '6px 8px',
                            fontWeight: 400,
                        }}
                    >
                        {`${getOptionNameByCodePriority(selectedItem.priority)} ${t('technicalSupport.priority').toLowerCase()}`}
                    </Box>
                </Stack>
            }
        >
            <Stack
                gap={2}
                overflow='auto'
            >
                <Stack
                    direction='row'
                    gap={1}
                    justifyContent='space-between'
                    sx={{flexWrap: 'wrap'}}
                >
                    <Stack className={styles.item}>
                        <ItemTitle title={t('username')} />
                        <Typography>{selectedItem.createdBy}</Typography>
                    </Stack>
                    <Stack className={styles.item}>
                        <ItemTitle title={t('technicalSupport.type')} />
                        <Typography>{getOptionNameByCodeType(selectedItem.type)}</Typography>
                    </Stack>
                    <Stack className={styles.item}>
                        <ItemTitle title={t('technicalSupport.section')} />
                        <Typography>{selectedItem.section}</Typography>
                    </Stack>
                </Stack>
                <Stack flexGrow={1}>
                    <ItemTitle title={t('description')} />
                    <Typography>{selectedItem.description}</Typography>
                </Stack>
                <TechnicalSupportIssueStatus
                    currentStatus={currentStatus}
                    afterSave={afterSaveStatus}
                    id={selectedItem.id}
                />
                <TechnicalSupportIssueStatusHistory histories={selectedItem.histories} />
                <ItemTitle title={t('files')} />
                {selectedItem.files.map((file) => (
                    <FileCard
                        key={file.id}
                        category={null}
                        file={
                            {
                                ...file,
                                documentDate: file.uploadDate || null,
                                originalFilename: file.filename || '',
                                _links: {
                                    'Скачать файл': {
                                        href: `files/${file.id}/download`,
                                    },
                                },
                            } as FileEntity
                        }
                        width='100%'
                        needEdit={false}
                    />
                ))}
            </Stack>
        </SidebarDialog>
    );
};
